import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import EmojiKeyboard from '../../../emojis/components/EmojiKeyboard';
import { emojiKey, useIsNarrow } from '../../../emojis/components/emojiCatalogShared';
import { fitEmojiPx } from '../../../emojis/components/emojiFit';
import { formatMinutes, isAuthorSlot, isOpenSlot, QuizBoard, SLOTS_PER_ROW } from './quizModel';
import type { QuizPrizeDto } from '../../api/quizTypes';
import { GuessTiles, QUIZ_COLORS } from './Tiles';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RowLabel = styled.span`
  width: 52px;
  font-size: 0.68rem;
  font-style: italic;
  color: ${QUIZ_COLORS.label};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Closed = styled.span`
  width: 44px;
  height: 44px;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  background: repeating-linear-gradient(45deg, #f3f4f6 0 4px, #ffffff 4px 8px);
`;

const Slot = styled.button<{ $author: boolean; $active: boolean; $bad: boolean; $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px solid ${(p) => (p.$bad ? '#dc2626' : p.$author ? QUIZ_COLORS.yellow : QUIZ_COLORS.brick)};
  border-radius: 4px;
  background: ${(p) => (p.$author ? QUIZ_COLORS.yellowWash : QUIZ_COLORS.brickWash)};
  box-shadow: ${(p) => (p.$active ? '0 0 0 2px rgb(35, 35, 35)' : 'none')};
  color: ${QUIZ_COLORS.label};
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  appearance: none;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

const KeyButton = styled.button`
  border: 0;
  background: transparent;
  appearance: none;
  padding: 6px 8px;
  font-size: 0.8rem;
  color: ${QUIZ_COLORS.ink2};
  cursor: pointer;
`;

const LockButton = styled.button`
  border: 1.5px solid rgb(35, 35, 35);
  box-shadow: 3px 3px rgba(0, 0, 0, 0.6);
  border-radius: 1px;
  background: #60fac1;
  padding: 6px 12px;
  font-size: 0.85rem;
  color: rgb(35, 35, 35);
  cursor: pointer;
  white-space: nowrap;

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.6);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Note = styled.div<{ $error?: boolean }>`
  margin-top: 6px;
  min-height: 1rem;
  font-size: 0.72rem;
  font-style: ${(p) => (p.$error ? 'normal' : 'italic')};
  color: ${(p) => (p.$error ? '#dc2626' : QUIZ_COLORS.label)};
  text-align: center;
`;

interface GuessBoxProps {
  board: QuizBoard;
  picks: (string | null)[];
  onPicksChange: (picks: (string | null)[]) => void;
  lockedAtMs: number | null;
  /** Posts the guess; resolves to the refusal to show, or null when it locked. */
  onLock: () => Promise<string | null>;
  /** A lock is on its way to the server. */
  busy?: boolean;
  closed: boolean;
  /** Under the answer once the quiz closed: who won the card, if anyone. */
  prize?: { winner: string; card: QuizPrizeDto } | null;
  /** Your guess was unlocked by a heist. */
  reopened?: boolean;
  /** Second Guess was played: one more lock to go. */
  secondTry?: boolean;
  /** The clock ran out before the close task ran: no more locks. */
  timeUp?: boolean;
}

const GuessBox: FC<GuessBoxProps> = ({ board, picks, onPicksChange, lockedAtMs, onLock, busy, closed, prize, reopened, secondTry, timeUp }) => {
  const [error, setError] = useState<{ index: number; text: string } | null>(null);
  const [shake, setShake] = useState(0);
  // the box being filled while the registry keyboard is open
  const [active, setActive] = useState<number | null>(null);
  const narrow = useIsNarrow();
  const locked = lockedAtMs !== null;
  const multi = board.rows.length > 1;

  const openSlots = useMemo(
    () => Array.from({ length: board.rows.length * SLOTS_PER_ROW }, (_, i) => i).filter((i) => isOpenSlot(board.rows, i)),
    [board.rows],
  );
  const usedByRow = useMemo(() => board.rows.map((r) => new Set(r.usedEmojis.map(emojiKey))), [board.rows]);
  // an author's earlier emojis can't be in today's answer: dimmed on the keyboard
  const blockedReason = useCallback(
    (emoji: string): string | null => {
      if (active === null || isAuthorSlot(active)) return null;
      const r = Math.floor(active / SLOTS_PER_ROW);
      const row = board.rows[r];
      return row && usedByRow[r]?.has(emojiKey(emoji))
        ? `${row.author} already used ${emoji} in an earlier AOTD, so it can't be in this one.`
        : null;
    },
    [active, board.rows, usedByRow],
  );
  const placed = useMemo(() => picks.filter((p): p is string => !!p), [picks]);
  const editable = lockedAtMs === null && !busy;
  // a lock, or the clock running out, closes the keyboard
  useEffect(() => {
    if (!editable) setActive(null);
  }, [editable]);

  if (closed) {
    const answer = board.answer ?? [];
    return (
      <div className="flex flex-col items-center" style={{ gap: 4 }}>
        <GuessTiles rows={board.rows} picks={answer} size={34} marks={answer.map((a, i) => (isOpenSlot(board.rows, i) ? 'exact' : 'closed'))} />
        <Note>
          {prize ? `the answer. ${prize.winner} won ${prize.card.cardEmoji} ${prize.card.cardName}` : 'the answer'}
        </Note>
      </div>
    );
  }

  const fail = (index: number, text: string) => {
    setError({ index, text });
    setShake((n) => n + 1);
  };

  const lock = async () => {
    if (busy) return;
    if (!picks.some(Boolean)) return fail(-1, 'Put at least one emoji in first.');
    setError(null);
    setActive(null);
    const refused = await onLock();
    if (refused) fail(-1, refused);
  };

  const choose = (i: number) => {
    if (!editable) return;
    setError(null);
    setActive(i);
  };

  // Tap a box, then an emoji on the registry keyboard: it lands in that box and
  // the next empty box lights up. The keyboard closes when every box is full,
  // so the lock button is in view.
  const place = (emoji: string) => {
    if (active === null) return;
    const k = emojiKey(emoji);
    const next = Array.from({ length: board.rows.length * SLOTS_PER_ROW }, (_, i) => picks[i] ?? null);
    // no brick box on the board can repeat an emoji, across rows too (a heist
    // joins both authors' emojis without repeats): picking one again moves it
    if (!isAuthorSlot(active)) {
      next.forEach((p, i) => {
        if (i !== active && !isAuthorSlot(i) && p && emojiKey(p) === k) next[i] = null;
      });
    }
    next[active] = emoji;
    onPicksChange(next);
    const after = openSlots.find((i) => i > active && !next[i]) ?? openSlots.find((i) => !next[i]);
    setActive(after ?? null);
  };

  // like a keyboard's delete key: empties the box, or steps back to the one before
  const erase = () => {
    if (active === null) return;
    const next = [...picks];
    if (next[active]) {
      next[active] = null;
      onPicksChange(next);
      return;
    }
    const before = [...openSlots].reverse().find((i) => i < active);
    if (before === undefined) return;
    next[before] = null;
    onPicksChange(next);
    setActive(before);
  };

  const slotLabel = (i: number) => {
    const row = board.rows[Math.floor(i / SLOTS_PER_ROW)];
    return isAuthorSlot(i) ? `${row?.author ?? 'the author'}'s own emoji` : `emoji ${(i % SLOTS_PER_ROW) + 1}`;
  };

  const renderSlot = (i: number, size: number, mirror = false) => {
    const author = isAuthorSlot(i);
    const pick = picks[i];
    return (
      <Slot
        key={i}
        type="button"
        onClick={() => choose(i)}
        // keep the page from scrolling to a focused box under the sheet
        onMouseDown={(e) => e.preventDefault()}
        disabled={!editable}
        aria-label={mirror ? undefined : slotLabel(i)}
        aria-pressed={active === i}
        $author={author}
        $active={active === i}
        $bad={error?.index === i}
        $size={size}
        style={{ marginLeft: author ? 8 : 0 }}
      >
        {pick ? <span style={{ fontSize: fitEmojiPx(size * 0.48), color: 'rgb(35, 35, 35)' }}>{pick}</span> : '?'}
      </Slot>
    );
  };

  const activeRow = active === null ? 0 : Math.floor(active / SLOTS_PER_ROW);
  const keyboardHeader =
    active === null ? null : (
      <div className="flex shrink-0 items-center justify-between gap-2 pb-1">
        {narrow ? (
          <div className="flex items-center" style={{ gap: 4 }}>
            {multi && <RowLabel title={board.rows[activeRow]?.author}>{board.rows[activeRow]?.author}</RowLabel>}
            {openSlots.filter((i) => Math.floor(i / SLOTS_PER_ROW) === activeRow).map((i) => renderSlot(i, 36, true))}
          </div>
        ) : (
          <span className="text-xs italic" style={{ color: QUIZ_COLORS.label }}>
            {slotLabel(active)}
          </span>
        )}
        <div className="flex items-center">
          <KeyButton type="button" onMouseDown={(e) => e.preventDefault()} onClick={erase} aria-label="delete" style={{ fontSize: '1.15rem' }}>
            ⌫
          </KeyButton>
          <KeyButton type="button" onClick={() => setActive(null)}>
            done
          </KeyButton>
        </div>
      </div>
    );

  return (
    <div className="relative flex flex-col items-center">
      {/* over the desktop keyboard's click-away layer, so another box can be picked */}
      <div className="flex items-center" style={{ gap: 12, position: 'relative', zIndex: active !== null && !narrow ? 45 : undefined }}>
        <motion.div
          key={shake}
          animate={shake ? { x: [0, -5, 5, -3, 3, 0] } : undefined}
          transition={{ duration: 0.3 }}
          className="flex flex-col"
          style={{ gap: 6 }}
        >
          {board.rows.map((row, r) => (
            <Row key={r}>
              {multi && <RowLabel title={row.author}>{row.author}</RowLabel>}
              {Array.from({ length: SLOTS_PER_ROW }, (_, p) => {
                const i = r * SLOTS_PER_ROW + p;
                if (!isOpenSlot(board.rows, i)) {
                  return <Closed key={i} title={isAuthorSlot(i) ? 'this author has no emoji of their own' : 'this author set fewer emojis'} />;
                }
                return renderSlot(i, 44);
              })}
            </Row>
          ))}
        </motion.div>
        {!locked && !timeUp && (
          <LockButton type="button" onClick={lock} disabled={busy}>
            lock
          </LockButton>
        )}
      </div>
      <Note $error={!!error}>
        {error
          ? error.text
          : locked
            ? `locked at ${formatMinutes(lockedAtMs as number)}, it can't change now`
            : timeUp
              ? "time's up before you locked a guess."
              : secondTry
              ? 'second guess: change what you like and lock again. the better of your two counts.'
              : reopened
              ? 'the art was stolen: your earlier guess is back, change it if you like'
              : board.rows[0]?.hasSignature === false
                ? 'brick boxes: the emojis, in order. wrong ones cost points.'
                : `brick boxes: the emojis, in order. yellow: ${board.rows[0]?.author ?? 'the author'}'s own emoji. wrong ones cost points.`}
      </Note>
      <EmojiKeyboard
        open={active !== null}
        onClose={() => setActive(null)}
        onPick={place}
        mode="pick"
        stayOpen
        preload={editable}
        blockedReason={blockedReason}
        selected={placed}
        header={keyboardHeader}
        title="Guess the emojis"
      />
    </div>
  );
};

export default GuessBox;
